// EquipmentsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, message, Input, Form, Select, Row, Col, Tabs, Spin, Collapse, Divider, Upload } from 'antd';
import { SearchOutlined, EditOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { Box } from '@mui/material';
import { jsPDF } from 'jspdf';
import companyLogo from '../../../assets/company-logo.png'; // Adjust the path as needed
import api from '../../../authentication/axiosInstance';
import { ArchiveWithAllocationsButton, UnarchiveButton, formatArchivedBy } from '../../../components/allocation/ArchiveWithAllocationsModal';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';  // Add this import at the top of your file
import useResizableSortableTable from '../../../hooks/useResizableSortableTable';
import DocumentThumbnail from '../../../components/DocumentThumbnail';
import DdPlowFormModal from './DdPlowFormModal';
import EquipmentCreateModal from './EquipmentCreateModal';
import EquipmentDetailsFields from './EquipmentDetailsFields';
const { Option } = Select;

const { Search } = Input;
const FLEETIO_DOUBLE_DOWN_EQUIPMENT_TYPE_ID = 1291028;
const FLEETIO_DOUBLE_DOWN_EQUIPMENT_TYPE_NAME = 'spreader - double down';
const FLEETIO_PLOW_EQUIPMENT_TYPE_IDS = [1113063, 1113064, 1113065, 1113066, 1113067, 1329706];
const FLEETIO_PLOW_EQUIPMENT_TYPE_NAME_PREFIX = 'plow';

const shouldShowEquipmentInDatabase = (item) => {
  const typeId = Number(item?.equipment_type_id);
  const typeName = (item?.equipment_type_name || '').trim().toLowerCase();
  if (typeId === FLEETIO_DOUBLE_DOWN_EQUIPMENT_TYPE_ID || typeName === FLEETIO_DOUBLE_DOWN_EQUIPMENT_TYPE_NAME) {
    return false;
  }
  if (FLEETIO_PLOW_EQUIPMENT_TYPE_IDS.includes(typeId) || typeName.startsWith(FLEETIO_PLOW_EQUIPMENT_TYPE_NAME_PREFIX)) {
    return false;
  }
  return true;
};

const normalizePlowRow = (plow) => ({
  key: `plow-${plow.plow_id}`,
  itemType: 'plow',
  itemId: plow.plow_id,
  kind: 'Plow',
  name: plow.plow_name,
  type: plow.plow_type,
  brand: plow.plow_brand,
  model: plow.plow_model,
  serial: plow.plow_serial_number,
  assignedVehicle: plow.assigned_vehicle?.vehicle_name || '—',
  archived_at: plow.archived_at,
  archived_by: plow.archived_by,
  raw: plow,
});

const normalizeDoubleDownRow = (doubleDown) => ({
  key: `dd-${doubleDown.doubleDown_id}`,
  itemType: 'doubledown',
  itemId: doubleDown.doubleDown_id,
  kind: 'DoubleDown',
  name: doubleDown.doubleDown_name,
  type: doubleDown.doubleDown_type_name,
  brand: doubleDown.doubleDown_brand,
  model: doubleDown.doubleDown_model,
  serial: '—',
  assignedVehicle: doubleDown.assigned_vehicle?.vehicle_name || '—',
  archived_at: doubleDown.archived_at,
  archived_by: doubleDown.archived_by,
  raw: doubleDown,
});

const matchesSearch = (value, search) => !search || (value || '').toLowerCase().includes(search.toLowerCase());

export const filterDdPlowRows = (rows, { searchName, searchBrand, searchModel }) => rows.filter((row) => (
  matchesSearch(row.name, searchName)
  && matchesSearch(row.brand, searchBrand)
  && matchesSearch(row.model, searchModel)
));

const EquipmentsPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [ddPlowRows, setDdPlowRows] = useState([]);
  const [archiveTab, setArchiveTab] = useState('active');
  const [loading, setLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [ddPlowForm, setDdPlowForm] = useState(null);
  const [isCreateEquipmentOpen, setIsCreateEquipmentOpen] = useState(false);

  // Added these new state variables for individual filters
  const [searchName, setSearchName] = useState('');
  const [searchBrand, setSearchBrand] = useState(undefined);
  const [searchModel, setSearchModel] = useState('');
  const [searchType, setSearchType] = useState(undefined);
  const [searchStatus, setSearchStatus] = useState(undefined);
  const [searchSeason, setSearchSeason] = useState(undefined);

  const applyFilters = () => {
    const filtered = equipment.filter((item) => {
      const filterName = item.equipment_name.toLowerCase().includes(searchName.toLowerCase());
      const filterBrand = searchBrand ? item.equipment_brand.toLowerCase().includes(searchBrand.toLowerCase()) : true;
      const filterModel = item.equipment_model.toLowerCase().includes(searchModel.toLowerCase());
      const filterType = searchType ? item.equipment_type_name.toLowerCase().includes(searchType.toLowerCase()) : true;
      const filterStatus = searchStatus ? item.equipment_status_name.toLowerCase().includes(searchStatus.toLowerCase()) : true;
      const filterSeason = searchSeason !== undefined ? (
        searchSeason === '' ? !item.season : item.season === searchSeason
      ) : true;
      return filterName && filterBrand && filterModel && filterType && filterStatus && filterSeason;
    });
    setFilteredEquipment(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [searchName, searchBrand, searchModel, searchType, searchStatus, searchSeason]);

  const isDdPlowTab = archiveTab === 'ddplows' || archiveTab === 'ddplows-archived';
  const wasDdPlowTabRef = useRef(isDdPlowTab);

  useEffect(() => {
    const changedFilterGroup = isDdPlowTab !== wasDdPlowTabRef.current;
    if (changedFilterGroup) {
      setSearchName('');
      setSearchBrand(undefined);
      setSearchModel('');
      setSearchType(undefined);
      setSearchStatus(undefined);
    }
    wasDdPlowTabRef.current = isDdPlowTab;
  }, [isDdPlowTab]);

  // Fetch equipment data from the API
  const fetchEquipment = async () => {
    setLoading(true);
    try {
      const response = await api.get('/equipment/', {
        params: { archived: archiveTab === 'archived' },
      });
      const visibleEquipment = response.data.filter(shouldShowEquipmentInDatabase);
      setEquipment(visibleEquipment);
      setFilteredEquipment(visibleEquipment);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      message.error('Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  };

  // Fetch merged plow + double-down rows for the DDs/Plows tabs
  const fetchDdPlows = async () => {
    setLoading(true);
    try {
      const archived = archiveTab === 'ddplows-archived';
      const [plowsResponse, doubleDownsResponse] = await Promise.all([
        api.get('/plows/', { params: { archived } }),
        api.get('/double-down/', { params: { archived } }),
      ]);
      const rows = [
        ...plowsResponse.data.map(normalizePlowRow),
        ...doubleDownsResponse.data.map(normalizeDoubleDownRow),
      ].sort((a, b) => `${a.kind} ${a.name}`.localeCompare(`${b.kind} ${b.name}`));
      setDdPlowRows(rows);
    } catch (error) {
      console.error('Failed to fetch plows/double-downs:', error);
      message.error('Failed to fetch plows/double-downs');
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrentTab = () => {
    if (isDdPlowTab) {
      fetchDdPlows();
    } else {
      fetchEquipment();
    }
  };

  useEffect(() => {
    if (isDdPlowTab) {
      fetchDdPlows();
    } else {
      fetchEquipment();
    }
  }, [archiveTab]);

  // Filter equipment based on search text
  // const filterEquipment = (value) => {
  //   const lowercasedValue = value.toLowerCase();
  //   const filtered = equipment.filter((item) =>
  //     item.equipment_name.toLowerCase().includes(lowercasedValue) ||
  //     item.equipment_brand.toLowerCase().includes(lowercasedValue) ||
  //     item.equipment_model.toLowerCase().includes(lowercasedValue) ||
  //     item.equipment_serial_number.toLowerCase().includes(lowercasedValue)
  //   );
  //   setFilteredEquipment(filtered);
  // };


  // Handle editing of equipment
  const [activeEquipmentTabKey, setActiveEquipmentTabKey] = useState('details');
  const [activeViewTabKey, setActiveViewTabKey] = useState('details');

  // States for Fleetio history and files
  const [equipmentFleetioFiles, setEquipmentFleetioFiles] = useState({});
  const [equipmentFleetioHistory, setEquipmentFleetioHistory] = useState({});
  const [equipmentLoadingFiles, setEquipmentLoadingFiles] = useState(false);
  const [equipmentLoadingHistory, setEquipmentLoadingHistory] = useState({});
  // Removed Fleetio data states (meter entries, expenses, submitted inspections) 
  // per PM requirements - data preserved in DB/API, hidden from UI
  const [equipmentFilesLoaded, setEquipmentFilesLoaded] = useState(false);
  const [equipmentHistoryLoaded, setEquipmentHistoryLoaded] = useState({});

  const showEditModal = (record) => {
    setEditingEquipment(record);
    setActiveEquipmentTabKey('details'); // Reset to first tab
    setEquipmentFleetioFiles({});
    setEquipmentFleetioHistory({});
    setEquipmentFilesLoaded(false);
    setEquipmentHistoryLoaded({});
    setIsEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalVisible(false);
    setEditingEquipment(null);
    setActiveEquipmentTabKey('details');
    setEquipmentFleetioFiles({});
    setEquipmentFleetioHistory({});
    setEquipmentFilesLoaded(false);
    setEquipmentHistoryLoaded({});
  };

  const handleEditSubmit = async (values) => {
    try {
      // Include the equipment_id in the update request
      const updatedEquipment = {
        ...values,
        equipment_id: editingEquipment.equipment_id
      };

      await api.put(`/equipment/${editingEquipment.equipment_id}/`, updatedEquipment);
      message.success('Equipment updated successfully');
      fetchEquipment();
      handleEditModalClose();
    } catch (error) {
      console.error('Failed to update equipment:', error);
      if (error.response && error.response.data) {
        console.error('Error details:', error.response.data);
      }
      message.error('Failed to update equipment');
    }
  };

  const handleEquipmentEditFormFailed = ({ errorFields }) => {
    // Switch to the tab containing the first validation error
    if (errorFields && errorFields.length > 0) {
      const firstErrorField = errorFields[0].name[0];
      
      // Map fields to their respective tabs
      const fieldToTabMap = {
        equipment_name: 'details',
        equipment_brand: 'details',
        equipment_model: 'details',
        equipment_serial_number: 'details',
        equipment_type_name: 'details',
        equipment_status_name: 'details',
        equipment_group_name: 'details',
        walks_coverage_rate: 'details',
        lots_coverage_rate: 'details',
        travel_km: 'details',
        flag_type: 'details',
        staff_limit: 'details',
      };
      
      const targetTab = fieldToTabMap[firstErrorField];
      if (targetTab) {
        setActiveEquipmentTabKey(targetTab);
        message.error('Please check the Details tab for errors');
      }
    }
  };

  // Fetch Fleetio files for equipment
  const fetchEquipmentFiles = async (equipmentId) => {
    if (equipmentFilesLoaded) return;
    
    setEquipmentLoadingFiles(true);
    try {
      const response = await api.get(`/equipment/${equipmentId}/files/`);
      setEquipmentFleetioFiles(response.data);
      setEquipmentFilesLoaded(true);
    } catch (error) {
      console.error('Failed to fetch equipment files:', error);
      setEquipmentFleetioFiles({});
    } finally {
      setEquipmentLoadingFiles(false);
    }
  };

  // Fetch Fleetio history for equipment
  const fetchEquipmentHistory = async (equipmentId, kind) => {
    if (equipmentHistoryLoaded[kind]) return;
    
    setEquipmentLoadingHistory(prev => ({ ...prev, [kind]: true }));
    try {
      const response = await api.get(`/equipment/${equipmentId}/history/`, {
        params: { kind, page_size: 50 }
      });
      setEquipmentFleetioHistory(prev => ({ ...prev, [kind]: response.data.results || [] }));
      setEquipmentHistoryLoaded(prev => ({ ...prev, [kind]: true }));
    } catch (error) {
      console.error(`Failed to fetch equipment ${kind} history:`, error);
      setEquipmentFleetioHistory(prev => ({ ...prev, [kind]: [] }));
    } finally {
      setEquipmentLoadingHistory(prev => ({ ...prev, [kind]: false }));
    }
  };

  // Removed Fleetio data fetching functions (data still accessible via API,
  // not shown in UI per PM requirements)

  // Handle tab change for equipment (simplified per PM requirements)
  const handleEquipmentTabChange = (key) => {
    setActiveEquipmentTabKey(key);
    
    if (!editingEquipment) return;
    
    const equipmentId = editingEquipment.equipment_id;
    
    // Lazy load files for the Documents & Photos tab only
    if (key === 'files' && !equipmentFilesLoaded) {
      fetchEquipmentFiles(equipmentId);
    }
  };

  // Handle viewing equipment details
  const showEquipmentDetails = (record) => {
    setSelectedEquipment(record);
    setIsModalVisible(true);
    setActiveViewTabKey('details');
    // Reset lazy-loaded data
    setEquipmentFleetioFiles({});
    setEquipmentFleetioHistory({});
    setEquipmentFilesLoaded(false);
    setEquipmentHistoryLoaded({});
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedEquipment(null);
    setActiveViewTabKey('details');
    // Reset lazy-loaded data
    setEquipmentFleetioFiles({});
    setEquipmentFleetioHistory({});
    setEquipmentFilesLoaded(false);
    setEquipmentHistoryLoaded({});
  };

  // Handle view modal tab change - lazy load data (simplified per PM requirements)
  const handleViewTabChange = (key) => {
    setActiveViewTabKey(key);
    
    if (!selectedEquipment) return;
    
    const equipmentId = selectedEquipment.equipment_id;
    
    // Lazy load files for the Documents & Photos tab only
    if (key === 'files' && !equipmentFilesLoaded) {
      fetchEquipmentFiles(equipmentId);
    }
  };

  // Documents + photos, shared by the view and edit modals (PM-kept fields only)
  const handleEquipmentFileUpload = async (file, category) => {
    if (!selectedEquipment) return false;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    
    try {
      await api.post(`/equipment/${selectedEquipment.equipment_id}/files/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      message.success(`${category === 'Document' ? 'Document' : 'Photo'} uploaded successfully`);
      fetchEquipmentFiles(selectedEquipment.equipment_id);
      return false;
    } catch (error) {
      console.error('Failed to upload file:', error);
      message.error(error.response?.data?.error || `Failed to upload ${category.toLowerCase()}`);
      return false;
    }
  };

  const renderEquipmentFiles = () => (
    <>
      <Divider orientation="left">Documents</Divider>
      <div style={{ marginBottom: '16px' }} data-testid="equipment-document-upload">
        <Upload
          beforeUpload={(file) => handleEquipmentFileUpload(file, 'Document')}
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload Document</Button>
        </Upload>
      </div>
      {equipmentLoadingFiles ? (
                  <div style={{ textAlign: 'center', padding: '48px' }}><Spin /></div>
                ) : (equipmentFleetioFiles['Document'] && equipmentFleetioFiles['Document'].length > 0) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '16px' }}>
                    {equipmentFleetioFiles['Document'].map((file, idx) => (
                      <DocumentThumbnail key={idx} file={file} />
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#999' }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>📄 Documents</p>
                    <p>No documents found</p>
                  </div>
                )}
      <Divider orientation="left">Photos</Divider>
      <div style={{ marginBottom: '16px' }} data-testid="equipment-photo-upload">
        <Upload
          beforeUpload={(file) => handleEquipmentFileUpload(file, 'Photo')}
          accept="image/*"
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />}>Upload Photo</Button>
        </Upload>
      </div>
      {equipmentLoadingFiles ? (
                  <div style={{ textAlign: 'center', padding: '48px' }}><Spin /></div>
                ) : (equipmentFleetioFiles['Photo'] && equipmentFleetioFiles['Photo'].length > 0) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                    {equipmentFleetioFiles['Photo'].map((file, idx) => (
                      <a key={idx} href={file.presigned_url} target="_blank" rel="noopener noreferrer">
                        <img src={file.presigned_url} alt={file.filename} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px' }} />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#999' }}>
                    <p style={{ fontSize: '16px', marginBottom: '8px' }}>📷 Photo Gallery</p>
                    <p>No photos found</p>
                  </div>
                )}
    </>
  );

  // Generate PDF for equipment details
  const generatePDF = (equipmentItem) => {
    if (!equipmentItem) {
      message.error('No equipment data available to generate PDF.');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const lineSpacing = 8;
    const indentSpacing = 10;

    // Add company logo
    if (companyLogo) {
      doc.addImage(companyLogo, 'PNG', margin, margin, 66, 13);
    }

    // Header
    doc.setFontSize(16);
    const headerText = `${equipmentItem.equipment_name} - Equipment Details`;
    const headerTextWidth = doc.getTextWidth(headerText);
    const rightAlignedX = pageWidth - headerTextWidth - 10;
    doc.text(headerText, rightAlignedX, 17);

    // Details
    doc.setFontSize(12);
    const xPos = margin;
    let yPos = 30;
    const details = [
      { label: 'Equipment Name:', value: equipmentItem.equipment_name },
      { label: 'Brand:', value: equipmentItem.equipment_brand || 'N/A' },
      { label: 'Model:', value: equipmentItem.equipment_model || 'N/A' },
      { label: 'Serial Number:', value: equipmentItem.equipment_serial_number || 'N/A' },
      { label: 'Type:', value: equipmentItem.equipment_type_name || 'N/A' },
      { label: 'Status:', value: equipmentItem.equipment_status_name || 'N/A' },
      { label: 'Group:', value: equipmentItem.equipment_group_name || 'N/A' },
      { label: 'Walks Coverage Rate:', value: equipmentItem.walks_coverage_rate },
      { label: 'Lots Coverage Rate:', value: equipmentItem.lots_coverage_rate },
      { label: 'Travel KM:', value: equipmentItem.travel_km || '0' },
      { label: 'Flag Type:', value: equipmentItem.flag_type || 'N/A' },
      { label: 'Staff Limit:', value: equipmentItem.staff_limit },
    ];

    details.forEach((detail) => {
      doc.text(`${detail.label} ${detail.value}`, xPos, yPos);
      yPos += lineSpacing;
    });

    doc.save(`${equipmentItem.equipment_name}_Details.pdf`);
  };

  // Define table columns
  const columns = [
    {
      title: 'Equipment Name',
      dataIndex: 'equipment_name',
      key: 'equipment_name',
      sortType: 'text',
    },
    {
      title: 'Brand',
      dataIndex: 'equipment_brand',
      key: 'equipment_brand',
      width: '11%',
      sortType: 'text',
    },
    {
      title: 'Model',
      dataIndex: 'equipment_model',
      key: 'equipment_model',
      width: '11%',
      sortType: 'text',
    },
    {
      title: 'Serial Number',
      dataIndex: 'equipment_serial_number',
      key: 'equipment_serial_number',
      width: '11%',
      sortType: 'text',
    },
    {
      title: 'Type',
      dataIndex: 'equipment_type_name',
      key: 'equipment_type_name',
      sortType: 'text',
    },
    {
      title: 'Status',
      dataIndex: 'equipment_status_name',
      key: 'equipment_status_name',
      width: '11%',
      sortType: 'text',
    },
    {
      title: 'Season',
      dataIndex: 'season',
      key: 'season',
      width: '10%',
      sortType: 'text',
      render: (season) => {
        if (!season) return 'N/A';
        if (season === 'year_round') return 'Year-round';
        return season.charAt(0).toUpperCase() + season.slice(1);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      sortable: false,
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="primary"
            onClick={() => showEquipmentDetails(record)}
            style={{ backgroundColor: '#cdaf5e', borderColor: '#cdaf5e' }}
          >
            View Details
          </Button>
          {archiveTab === 'active' ? (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => showEditModal(record)}
              >
                Edit
              </Button>
              <ArchiveWithAllocationsButton
                itemType="equipment"
                itemId={record.equipment_id}
                itemLabel={record.equipment_name}
                onArchived={fetchEquipment}
              />
            </>
          ) : (
            <UnarchiveButton
              itemType="equipment"
              itemId={record.equipment_id}
              itemLabel={record.equipment_name}
              onUnarchived={fetchEquipment}
            />
          )}
        </div>
      ),
    },
    ...(archiveTab === 'archived' ? [
      {
        title: 'Archived At',
        dataIndex: 'archived_at',
        key: 'archived_at',
        sortType: 'date',
        render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—',
      },
    ] : []),
  ];

  // Use the resizable and sortable table hook
  const { sortedData, enhancedColumns, ResizableTitle } = useResizableSortableTable(
    'equipment-table',
    columns,
    filteredEquipment
  );

  const visibleDdPlowRows = filterDdPlowRows(ddPlowRows, { searchName, searchBrand, searchModel });

  const brandOptions = Array.from(new Set(
    (isDdPlowTab ? ddPlowRows.map((row) => row.brand) : equipment.map((item) => item.equipment_brand))
      .filter(Boolean)
  ));

  const ddPlowColumns = [
    {
      title: 'Kind',
      dataIndex: 'kind',
      key: 'kind',
      width: '9%',
      sortType: 'text',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sortType: 'text',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sortType: 'text',
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: '10%',
      sortType: 'text',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: '10%',
      sortType: 'text',
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial',
      key: 'serial',
      width: '12%',
      sortType: 'text',
    },
    {
      title: 'Assigned Vehicle',
      dataIndex: 'assignedVehicle',
      key: 'assignedVehicle',
      sortType: 'text',
    },
    {
      title: 'Actions',
      key: 'actions',
      sortable: false,
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {archiveTab === 'ddplows' ? (
            <>
              <Button
                icon={<EditOutlined />}
                onClick={() => setDdPlowForm({ itemType: record.itemType, mode: 'edit', record })}
              >
                Edit
              </Button>
              <ArchiveWithAllocationsButton
                itemType={record.itemType}
                itemId={record.itemId}
                itemLabel={record.name}
                onArchived={refreshCurrentTab}
              />
            </>
          ) : (
            <UnarchiveButton
              itemType={record.itemType}
              itemId={record.itemId}
              itemLabel={record.name}
              onUnarchived={refreshCurrentTab}
            />
          )}
        </div>
      ),
    },
    ...(archiveTab === 'ddplows-archived' ? [
      {
        title: 'Archived At',
        dataIndex: 'archived_at',
        key: 'archived_at',
        sortType: 'date',
        render: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—',
      },
      {
        title: 'Archived By',
        dataIndex: 'archived_by',
        key: 'archived_by',
        sortType: 'text',
        render: (value) => formatArchivedBy(value),
      },
    ] : []),
  ];

  // Use the resizable and sortable table hook for DDs/Plows
  const { 
    sortedData: sortedDdPlowRows, 
    enhancedColumns: enhancedDdPlowColumns, 
    ResizableTitle: ResizableTitleDdPlow 
  } = useResizableSortableTable(
    'ddplow-table',
    ddPlowColumns,
    visibleDdPlowRows
  );

  return (
    <Box style={{ padding: 30 }}>
      <Box style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Equipments</h2>
        <div>
          <Input.Search
            placeholder="Search name"
            value={searchName}
            onSearch={setSearchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 200, marginRight: 8 }}
            prefix={<SearchOutlined />}
          />
          <Select
            placeholder="Search by brand"
            value={searchBrand}
            onChange={setSearchBrand}
            style={{ width: 200, marginRight: 8 }}
            allowClear
          >
            {brandOptions.map((brand) => (
              <Option key={brand} value={brand}>
                {brand}
              </Option>
            ))}
          </Select>
          <Input.Search
            placeholder="Search model"
            value={searchModel}
            onSearch={setSearchModel}
            onChange={(e) => setSearchModel(e.target.value)}
            style={{ width: 200, marginRight: 8 }}
            prefix={<SearchOutlined />}
          />
          {!isDdPlowTab && (
            <>
              <Select
                placeholder="Search by type"
                value={searchType}
                onChange={setSearchType}
                style={{ width: 200, marginRight: 8 }}
                allowClear
              >
                {Array.from(new Set(equipment.map((item) => item.equipment_type_name))) // Extract unique types
                  .map((type) => (
                    <Option key={type} value={type}>
                      {type}
                    </Option>
                ))}
              </Select>
              <Select
                placeholder="Search by status"
                value={searchStatus}
                onChange={setSearchStatus}
                style={{ width: 200, marginRight: 8 }}
                allowClear
              >
                {Array.from(new Set(equipment.map((item) => item.equipment_status_name))) // Extract unique statuses
                  .map((status) => (
                    <Option key={status} value={status}>
                      {status}
                    </Option>
                ))}
              </Select>
              <Select
                placeholder="Season"
                value={searchSeason}
                onChange={setSearchSeason}
                style={{ width: 150 }}
                allowClear
              >
                <Option value="winter">Winter</Option>
                <Option value="summer">Summer</Option>
                <Option value="year_round">Year-round</Option>
                <Option value="">Not set</Option>
              </Select>
            </>
          )}
        </div>
      </Box>
      <Tabs
        activeKey={archiveTab}
        onChange={setArchiveTab}
        style={{ marginBottom: 12 }}
        items={[
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' },
          { key: 'ddplows', label: 'DDs/Plows' },
          { key: 'ddplows-archived', label: 'Archived DDs/Plows' },
        ]}
      />
      {archiveTab === 'active' && (
        <Box style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateEquipmentOpen(true)}
          >
            Add equipment
          </Button>
        </Box>
      )}
      {archiveTab === 'ddplows' && (
        <Box style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setDdPlowForm({ itemType: 'plow', mode: 'create' })}
          >
            Add plow
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setDdPlowForm({ itemType: 'doubledown', mode: 'create' })}
          >
            Add double down
          </Button>
        </Box>
      )}
      {isDdPlowTab ? (
        <Table
          dataSource={sortedDdPlowRows}
          columns={enhancedDdPlowColumns}
          rowKey="key"
          loading={loading}
          pagination={false}
          scroll={{ y: 640 }}
          components={{
            header: {
              cell: ResizableTitleDdPlow,
            },
          }}
        />
      ) : (
        <Table
          dataSource={sortedData}
          columns={enhancedColumns}
          rowKey="equipment_id"
          loading={loading}
          pagination={false}
          scroll={{ y: 640 }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
        />
      )}

      {/* Equipment Details Modal */}
      <Modal
        title="Equipment Details"
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="pdf" type="primary" onClick={() => generatePDF(selectedEquipment)}>
            Download PDF
          </Button>,
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={800}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {selectedEquipment && (
          <Tabs
            activeKey={activeViewTabKey}
            onChange={handleViewTabChange}
            destroyInactiveTabPane={false}
            items={[
              {
                key: 'details',
                label: 'Details',
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Equipment Name:</strong>
                          <div>{selectedEquipment.equipment_name}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Brand:</strong>
                          <div>{selectedEquipment.equipment_brand || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Model:</strong>
                          <div>{selectedEquipment.equipment_model || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Serial Number:</strong>
                          <div>{selectedEquipment.equipment_serial_number || 'N/A'}</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Type:</strong>
                          <div>{selectedEquipment.equipment_type_name || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Status:</strong>
                          <div>{selectedEquipment.equipment_status_name || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Group:</strong>
                          <div>{selectedEquipment.equipment_group_name || 'N/A'}</div>
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Walks Coverage Rate:</strong>
                          <div>{selectedEquipment.walks_coverage_rate}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Lots Coverage Rate:</strong>
                          <div>{selectedEquipment.lots_coverage_rate}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Travel KM:</strong>
                          <div>{selectedEquipment.travel_km || '0'}</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Flag Type:</strong>
                          <div>{selectedEquipment.flag_type || 'N/A'}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Staff Limit:</strong>
                          <div>{selectedEquipment.staff_limit}</div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <strong>Season:</strong>
                          <div>{selectedEquipment.season ? (selectedEquipment.season === 'year_round' ? 'Year-round' : selectedEquipment.season.charAt(0).toUpperCase() + selectedEquipment.season.slice(1)) : 'N/A'}</div>
                        </div>
                      </Col>
                    </Row>
                  </>
                ),
              },
              {
                key: 'files',
                label: 'Documents & Photos',
                children: renderEquipmentFiles(),
              },

            ]}
          />
        )}
      </Modal>

      {/* Edit Equipment Modal */}
      <Modal
        title="Edit Equipment"
        visible={isEditModalVisible}
        onCancel={handleEditModalClose}
        footer={null}
        width={800}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {editingEquipment && (
          <Form
            key={editingEquipment.equipment_id}
            initialValues={editingEquipment}
            onFinish={handleEditSubmit}
            onFinishFailed={handleEquipmentEditFormFailed}
            layout="vertical"
          >
            <Tabs
              activeKey={activeEquipmentTabKey}
              onChange={handleEquipmentTabChange}
              destroyInactiveTabPane={false}
              items={[
                {
                  key: 'details',
                  label: 'Details',
                  children: <EquipmentDetailsFields />,
                },
                {
                  key: 'files',
                  label: 'Documents & Photos',
                  children: renderEquipmentFiles(),
                },

              ]}
            />

            <Form.Item style={{ marginTop: '24px' }}>
              <Button type="primary" htmlType="submit" size="large" block>
                Update Equipment
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <EquipmentCreateModal
        open={isCreateEquipmentOpen}
        onClose={() => setIsCreateEquipmentOpen(false)}
        onCreated={() => {
          setIsCreateEquipmentOpen(false);
          fetchEquipment();
        }}
      />

      <DdPlowFormModal
        itemType={ddPlowForm?.itemType}
        mode={ddPlowForm?.mode}
        record={ddPlowForm?.record}
        onClose={() => setDdPlowForm(null)}
        onSaved={() => {
          setDdPlowForm(null);
          fetchDdPlows();
        }}
      />
    </Box>
  );
};

export default EquipmentsPage;
